import React from 'react';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';

type Props = {
  /** Names the failed subtree in the error report, e.g. the card or section. */
  label: string;
  /**
   * Identity of the content being rendered. A new value clears the failure, so
   * fixing bad data recovers the subtree without a remount, while re-rendering
   * the same content cannot retry into a loop.
   */
  resetKey: unknown;
  /**
   * Admin surfaces (offer list, offer and content editors, designer, branding)
   * show a visible message, because blank space there is untraceable for the
   * person who can actually fix the content. Customer-facing surfaces render
   * nothing rather than stopping someone part-way through cancelling.
   */
  isAdminSurface: boolean;
  children: React.ReactNode;
};

/**
 * Contains a render failure to the card or page section that caused it.
 *
 * Content JSON whose shape does not match what its template's renderer expects
 * throws during render. Unconstrained, that unmounts the whole funnel or the
 * entire list of admin previews, and on admin screens nothing below the router
 * would stop it.
 *
 * Only render-phase and lifecycle errors are caught. Throws from event handlers
 * and async callbacks still escape and need handling at the callsite.
 */
export class RenderErrorBoundary extends React.Component<
  Props,
  { hasError: boolean }
> {
  state = { hasError: false };

  /** Set while a failure is being shown, holding the content that caused it. */
  private failure: { resetKey: unknown } | undefined = undefined;

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    const { label, resetKey } = this.props;
    this.failure = { resetKey };
    GlobalLogger.error(`Render failed: ${label}`, error);
  }

  componentDidUpdate() {
    const { hasError } = this.state;
    const { resetKey } = this.props;

    if (hasError && this.failure && this.failure.resetKey !== resetKey) {
      // Cleared before the retry, because React runs componentDidUpdate before
      // componentDidCatch. Leaving the old key here would let the next failure
      // compare against it and retry a second time.
      this.failure = undefined;
      this.setState({ hasError: false });
    }
  }

  render() {
    const { hasError } = this.state;
    const { isAdminSurface, children } = this.props;

    if (hasError) {
      return isAdminSurface ? (
        <div className="tw-rounded-lg tw-border tw-border-dashed tw-border-neutral-300 tw-bg-neutral-50 tw-p-5 tw-text-center tw-text-sm tw-text-danger-500">
          This content could not be displayed. Its saved data does not match the
          selected template.
        </div>
      ) : null;
    }
    return children;
  }
}
